import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from "@angular/core";
import {AbstractControl, FormControl, FormGroup, Validators} from "@angular/forms";
import {debounceTime, finalize, switchMap} from "rxjs/internal/operators";
import {DpAutoSuggestService} from "./dp-auto-suggest.service";
import {AutoSuggest} from "./dp-auto-suggest";
import {Cirf} from "../../cirf/cirf";
import {of} from "rxjs";
import {CirfService} from "../../cirf/cirf.service";


@Component({
    selector: 'dp-connect-auto-suggest',
    templateUrl: './dp-connect-auto-suggest.html'
})
export class DpConnectAutoSuggestComponent implements OnInit {
    @Input() placeHolder: string;
    @Input() isMatchRequired: boolean = true;
    @Input() type: string;
    @Input() default: FormControl;
    @Input() autoFocus: Boolean = false;
    @Input() fullWidth: boolean = true;
    @Input() lawFirmId: number;
    @Input() isDropdownArrowVisible : boolean = false;
    @Input() showDeleteButton: boolean = false;
    @Input() showWarningMessage: boolean = false;
    @Input() warningMessage: string = '';
    @Input()
    set dropDownOptions(value: AutoSuggest[]) {
        this._dropDownOptions = value;
        this.autoSuggestList = value;
    }

    get dropDownOptions(): AutoSuggest[] {
        return this._dropDownOptions;
    }

    @Input()
    set isValidatorRequired(value: boolean) {
        if(this.formGroup && this._isValidatorRequired != value){
            this.applyValidation(value);
        }
        this._isValidatorRequired = value;
    }

    get isValidatorRequired() : boolean {
        return this._isValidatorRequired;
    }

    @Input()
    set searchTextValue(value: string) {
        this._searchTextValue = value;
        if(this.formGroup.controls.searchText && this.formGroup.controls.searchText.value) {
            //set outside searchText value
            if((this.formGroup.controls.searchText.value != this._searchTextValue)
                && (this._searchTextValue != this.formGroup.controls.searchText.value.autoSuggestValueCol1)) {
                this.setSearchText(value);
            }
        }
    }

    get searchTextValue(): string {
        return this._searchTextValue;
    }

    _searchTextValue: string

    autoSuggestList: any;
    formGroup: FormGroup;
    isLoading: boolean = false;
    _isAutoSuggestReadOnly : boolean = false;
    _isValidatorRequired: boolean = false;
    _dropDownOptions: AutoSuggest[]; //optional to pass drop down options to use instead of going to back-end
    placeHolderText: string;
    @Output() onOptionSelection = new EventEmitter();
    @ViewChild('dpAutoSuggest') dpAutoSuggest : ElementRef;
    @Output() deleteAction = new EventEmitter();

    constructor(private  dpAutoSuggestService: DpAutoSuggestService,
                public cirfService: CirfService) {
    }

    @Input() set readonly(isAutoSuggestReadOnly : boolean) {
        this._isAutoSuggestReadOnly = isAutoSuggestReadOnly;
        if ( this.formGroup) {
            if (!!isAutoSuggestReadOnly) {
                this.formGroup.disable();
            } else {
                this.formGroup.enable();
            }
        }
    }

    ngOnInit() {
        let ref = this;
        if(this.autoFocus) {
            setTimeout(() => {
                ref.dpAutoSuggest.nativeElement.focus();
            }, 500)
        }

        this.formGroup = new FormGroup({
            searchText: new FormControl()
        });
        if (!!this.default) {
            this.setDefaultAutoSuggestValue(this.default.value)
        }
        if(!!this._isAutoSuggestReadOnly){
            this.formGroup.disable();
        }

        this.applyValidation(this.isValidatorRequired);

        if (this._dropDownOptions) {
            this.autoSuggestList = this._dropDownOptions;
        } else {
            this.formGroup.get('searchText').valueChanges
                .pipe(
                    debounceTime(1000),
                    switchMap((value: string) => {
                        this.isLoading = true;
                        if (!!value && value != '' && typeof value == 'string') {
                            return this.dpAutoSuggestService.getAutoSuggestList(value, this.type, this.lawFirmId).pipe(
                                finalize(() => this.isLoading = false),
                            )
                        } else {
                            this.isLoading = false
                            return [];
                        }
                    })
                )
                .subscribe(response => {
                    this.autoSuggestList = response;
                });
        }
    }

    applyValidation(value: boolean) : void {
        if(value){
            if (this.isMatchRequired) {
                this.formGroup.get('searchText').setValidators(Validators.compose([Require, RequireMatch]));
            } else {
                this.formGroup.get('searchText').setValidators(Validators.compose([Require]));
            }
            this.placeHolderText = this.placeHolder + '*';
        } else {
            this.formGroup.get('searchText').clearValidators();
            this.placeHolderText = this.placeHolder;
        }
        this.formGroup.get('searchText').markAsTouched();
        this.formGroup.get('searchText').updateValueAndValidity();
    }

    optionSelected(event: any) {
        if (event.option.value.autoSuggestDisplayValue == 'NO_RECORD_FOUND') {
            this.formGroup.controls.searchText.setValue('');
            this.onOptionSelection.emit({value: 'NO_RECORD_FOUND'});
        }
        else if (event && event.option && event.option.value) {
            this.onOptionSelection.emit({
                value: event.option.value.autoSuggestDisplayValue,
                displayName: event.option.value.displayName,
                object: event.option.value.searchObject
            });
        }
    }

    setDefaultAutoSuggestValue(value: any): void {
        if (!!value) {
            this.formGroup.controls.searchText.setValue(new AutoSuggest(value, value));
        }
    }

    clearValue(): void {
       this.formGroup.controls.searchText.setValue('');
    }


    displayFn(autoSuggest: AutoSuggest): string {
        return autoSuggest && !!autoSuggest.displayName ? autoSuggest.displayName : '';
    }

    onFocusOut(event: any) : void{
        if(event && ((event.relatedTarget && event.relatedTarget.tagName != 'MAT-OPTION') || !event.relatedTarget) &&
        this.formGroup) {
            if (!!this.formGroup.controls.searchText.value && typeof this.formGroup.controls.searchText.value == 'string') {
                this.onOptionSelection.emit({
                    displayName: this.formGroup.controls.searchText.value,
                });
            }
            else if(!this.formGroup.controls.searchText.value || (this.formGroup.controls.searchText.value == 'string'
                && this.formGroup.controls.searchText.value.trim() == '')){
                this.onOptionSelection.emit({
                    displayName: '',
                });
            }
        }
    }

    showDropdownArrow(): boolean {
        return this.isDropdownArrowVisible && Array.isArray(this.autoSuggestList) && this.autoSuggestList.length > 0;
    }

    setSearchText(value) {
        this.formGroup.controls.searchText.setValue(new AutoSuggest(value, value));
    }

    delete() {
        this.deleteAction.emit();
    }
}

export function RequireMatch(control: AbstractControl) {
    const selection: any = control.value;
    if (typeof selection === 'string' && !!selection) {
        return {incorrect: true};
    }
    return null;
}

export function Require(control: AbstractControl) {
    const selection: AutoSuggest = control.value;
    if (!selection) {
        return {required: true};
    }
    return null;
}

